import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Unit, ApiResponse, PaginatedResponse, RoomType, Customer, LateFeePolicy, Lease, Invoice } from '@/types'
import { roomTypeKeys } from './useRoomTypes'
import { propertyKeys } from './useProperties'
import { leaseKeys } from './useLeases'
import { invoiceKeys } from './useInvoices'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { accountSettingsKeys } from './useAccountSettings'

// Extended unit type with relations
export interface PropertyUnit extends Unit {
  room_type: Pick<RoomType, 'id' | 'name' | 'floor'>
  leases?: {
    id: number
    lease_code: string
    started_at: string
    ended_at: string | null
    signed_at: string | null
    customer: Pick<Customer, 'id' | 'first_name' | 'last_name'> & { name: string }
  }[]
}

// Query keys
export const unitKeys = {
  all: ['units'] as const,
  lists: () => [...unitKeys.all, 'list'] as const,
  list: (roomTypeId: number) => [...unitKeys.lists(), roomTypeId] as const,
  propertyLists: () => [...unitKeys.all, 'property-list'] as const,
  propertyList: (propertyId: number, page: number, roomTypeId?: number) =>
    [...unitKeys.propertyLists(), propertyId, page, roomTypeId] as const,
  details: () => [...unitKeys.all, 'detail'] as const,
  detail: (id: number) => [...unitKeys.details(), id] as const,
}

// Fetch units for a room type
export function useUnits(roomTypeId: number) {
  return useQuery({
    queryKey: unitKeys.list(roomTypeId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Unit[]>>(`/api/room_types/${roomTypeId}/units`)
      return data.data
    },
    enabled: !!roomTypeId,
  })
}

// Fetch all units for a property with pagination
export function usePropertyUnits(
  propertyId: number,
  page: number = 1,
  roomTypeId?: number,
  /**
   * Server-side unit-number search. Without it a searchable dropdown can only
   * filter the page it already holds, so unit 21 onwards is unreachable behind a
   * list that looks complete. Pairs with SearchableDropdown's `onSearch` and its
   * `onLoadMore`/`hasMore` paging — the same shape `useCustomers` already uses.
   */
  search?: string,
) {
  return useQuery({
    queryKey: [...unitKeys.propertyList(propertyId, page, roomTypeId), search ?? ''] as const,
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), per_page: '20' })
      if (roomTypeId) params.append('room_type_id', String(roomTypeId))
      if (search) params.append('search', search)
      const { data } = await api.get<PaginatedResponse<PropertyUnit[]>>(
        `/api/properties/${propertyId}/units?${params}`
      )
      return data
    },
    enabled: !!propertyId,
  })
}

// Fetch single unit
export function useUnit(id: number) {
  return useQuery({
    queryKey: unitKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Unit>>(`/api/units/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create unit
interface CreateUnitInput {
  roomTypeId: number
  name: string
  price: number
  area_sqm?: number
  share_ratio?: number
}

export function useCreateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ roomTypeId, ...input }: CreateUnitInput) => {
      const { data } = await api.post<ApiResponse<Unit>>(`/api/room_types/${roomTypeId}/units`, {
        unit: input,
      })
      return { data: data.data, roomTypeId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, unitKeys.list(result.roomTypeId))
      invalidateDetailQueries(queryClient, roomTypeKeys.detail(result.roomTypeId))
      // PropertyDetailPage embeds room_types[].units[] in its property payload,
      // so the property detail cache must refetch to show the new/removed unit.
      invalidateDetailQueries(queryClient, propertyKeys.details())
    },
  })
}

// Create unit under a property (auto-resolves room type via backend S2-3 rule)
interface CreatePropertyUnitInput {
  propertyId: number
  name: string
  price: number
  area_sqm?: number
  share_ratio?: number
}

export function useCreatePropertyUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ propertyId, ...unit }: CreatePropertyUnitInput) => {
      const { data } = await api.post<ApiResponse<Unit>>(
        `/api/properties/${propertyId}/units`,
        { unit },
      )
      return data.data
    },
    onSuccess: () => {
      // A new unit changes both the property detail payload (room_types[].units[])
      // and the property list card counts, so invalidate both prefixes.
      queryClient.invalidateQueries({ queryKey: propertyKeys.details() })
      queryClient.invalidateQueries({ queryKey: propertyKeys.lists() })
    },
  })
}

// Update unit
interface UpdateUnitInput {
  id: number
  /** room_type the unit CURRENTLY lives under — used for list-cache invalidation. */
  roomTypeId: number
  name?: string
  price?: number
  area_sqm?: number
  share_ratio?: number
  /** When set, MOVE the unit to a different room_type (must be same property). */
  room_type_id?: number
  pricing_formula?: import('@/types').PricingFormula
  late_fee_policy?: LateFeePolicy | Record<string, never>
}

export function useUpdateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, roomTypeId, ...input }: UpdateUnitInput) => {
      const { data } = await api.patch<ApiResponse<Unit>>(`/api/units/${id}`, {
        unit: input,
      })
      // Capture the destination room_type for cache invalidation. If the
      // payload included `room_type_id`, the unit was moved — invalidate
      // BOTH lists. Otherwise the destination equals the source.
      const movedTo = input.room_type_id ?? roomTypeId
      return { data: data.data, roomTypeId, movedTo }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, unitKeys.list(result.roomTypeId))
      if (result.movedTo !== result.roomTypeId) {
        removeListQueries(queryClient, unitKeys.list(result.movedTo))
      }
      invalidateDetailQueries(queryClient, unitKeys.detail(result.data.id))
      invalidateDetailQueries(queryClient, propertyKeys.details())
    },
  })
}

// Delete unit
export function useDeleteUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, roomTypeId }: { id: number; roomTypeId: number }) => {
      await api.delete(`/api/units/${id}`)
      return { id, roomTypeId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, unitKeys.list(result.roomTypeId))
      invalidateDetailQueries(queryClient, propertyKeys.details())
      // Refresh per-entity counts so empty-state seed/affordance gating is accurate.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}

// Book a unit (EP-136 T136-021) — POST /api/units/:unitId/booking. One
// transaction on the server (UnitBookingService) creates a draft
// RentalLease + a one-line booking invoice and flips the unit to
// 'reserved'. amount and reservation_expires_at are optional — the
// service falls back to the property's resolved booking-fee default and
// to the end of the booking month respectively.
//
// 422 typed error.code values (booking_not_available_for_condominium_account,
// unit_occupied, unit_already_reserved, amount_required, amount_invalid,
// amount_exceeds_deposit) are forwarded untranslated — the axios error
// propagates as-is, callers translate via translateApiError.
interface CreateBookingInput {
  unitId: number
  customer_id: number
  started_at: string
  amount?: number
  reservation_expires_at?: string
}

interface BookingResult {
  lease: Lease
  invoice: Invoice
}

export function useCreateBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ unitId, ...input }: CreateBookingInput) => {
      const { data } = await api.post<ApiResponse<BookingResult>>(
        `/api/units/${unitId}/booking`,
        input
      )
      return data.data
    },
    onSuccess: (_, { unitId }) => {
      // A booking flips the unit's status/current_booking/bookable, spawns a
      // draft lease and a booking invoice — every one of those caches must
      // refresh, not just the unit. Room-type isn't known here, so the whole
      // unit-list prefix (both the room-type-scoped and property-scoped
      // shapes) is removed rather than one narrow key.
      removeListQueries(queryClient, unitKeys.lists())
      removeListQueries(queryClient, unitKeys.propertyLists())
      invalidateDetailQueries(queryClient, unitKeys.detail(unitId))
      removeListQueries(queryClient, leaseKeys.lists())
      removeListQueries(queryClient, invoiceKeys.lists())
      // Properties#show embeds each unit's status/current_booking/bookable —
      // without this the property detail page's unit grid keeps rendering
      // the pre-booking state (standing convention: invalidate the parent's
      // prefix too when a resource is embedded in a parent payload).
      invalidateDetailQueries(queryClient, propertyKeys.details())
    },
  })
}
